/* START_MODULE_CONTRACT
 * PURPOSE: Frontend UI module: EditProjectRoleDialog.
 * SCOPE: React component or page.
 * DEPENDS: M-FE-API, M-FE-AUTH
 * LINKS: M-FE-ROLES
 * END_MODULE_CONTRACT
 *
 * START_MODULE_MAP
 * EditProjectRoleDialog - Frontend UI module: EditProjectRoleDialog.
 * END_MODULE_MAP
 *
 * START_CHANGE_SUMMARY
 * LAST_CHANGE: 2026-07-02 - GRACE coverage: контракты функций, парные END_METHOD, блок отправки.
 * END_CHANGE_SUMMARY
 */

import { useState, useEffect, useRef } from 'react';
import Box from '@mui/material/Box';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import { useDialogFormReady } from '../../../hooks/useDialogFormReady';
import { useBlurValidatedField } from '../../../hooks/useBlurValidatedField';
import { isIntegerInRange, isNonEmptyTrimmed } from '../../../utils/formValidators';
import RoleWorkloadBar, { clampWorkloadPercent } from '../../../components/RoleWorkloadBar';
import { getColorByName } from '../../../utils/projectColors';
import ConcurrentRolesSelect from './ConcurrentRolesSelect';

const isNonEmpty = (value) => value !== '';

/* START_METHOD_EditProjectRoleDialog */
/* START_CONTRACT: EditProjectRoleDialog
 * PURPOSE: Диалог редактирования проектной роли с предзаполнением полей и совместных ролей.
 * INPUTS: { open: boolean, onClose: ()=>void, onUpdate: (id,payload)=>Promise, role: ProjectRole, existingRoles: ProjectRole[] }.
 * OUTPUTS: { JSX.Element|null - диалог }.
 * SIDE_EFFECTS: вызов onUpdate (HTTP наверх); локальный state.
 * LINKS: M-FE-ROLES
 * END_CONTRACT: EditProjectRoleDialog
 */
export default function EditProjectRoleDialog({
  open,
  onClose,
  onUpdate,
  role,
  existingRoles,
}) {
  const firstFieldRef = useRef(null);
  const { formReady, resetFormReady, dialogTransitionSlotProps } = useDialogFormReady({ firstFieldRef });
  const nameField = useBlurValidatedField('', isNonEmptyTrimmed, formReady);
  const shortNameField = useBlurValidatedField('', isNonEmptyTrimmed, formReady);
  const sortOrderField = useBlurValidatedField('', isNonEmpty, formReady);
  const workloadField = useBlurValidatedField('', isIntegerInRange(0, 100), formReady);
  const [concurrentRoles, setConcurrentRoles] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!role) return;
    nameField.reset(role.name);
    shortNameField.reset(role.shortName);
    sortOrderField.reset(String(role.sortOrder));
    workloadField.reset(String(role.workloadPercent));
    const selected = existingRoles.filter((r) => role.concurrentRoleIds.includes(r.id));
    setConcurrentRoles(selected);
  }, [role, existingRoles]);

  if (!role) return null;

  const canSave = nameField.isValid
    && shortNameField.isValid
    && sortOrderField.isValid
    && workloadField.isValid;
  const previewWorkload = workloadField.isValid
    ? clampWorkloadPercent(workloadField.value)
    : 0;
  const previewAccent = getColorByName(nameField.value.trim() || role.name);

  /* START_METHOD_handleClose */
  /* START_CONTRACT: handleClose
   * PURPOSE: Закрыть диалог (заблокировано во время сохранения).
   * INPUTS: {}.
   * OUTPUTS: { void }.
   * SIDE_EFFECTS: resetFormReady; onClose.
   * LINKS: M-FE-ROLES
   * END_CONTRACT: handleClose
   */
  const handleClose = () => {
    if (loading) return;
    resetFormReady();
    onClose();
  };
  /* END_METHOD_handleClose */

  /* START_METHOD_handleUpdate */
  /* START_CONTRACT: handleUpdate
   * PURPOSE: Собрать payload и обновить проектную роль; при успехе закрыть диалог.
   * INPUTS: { event?: React.FormEvent }.
   * OUTPUTS: { Promise<void> }.
   * SIDE_EFFECTS: onUpdate (HTTP); setLoading; onClose.
   * LINKS: M-FE-ROLES
   * END_CONTRACT: handleUpdate
   */
  const handleUpdate = async (event) => {
    if (event) event.preventDefault();
    if (!canSave || loading) {
      /* START_BLOCK_SUBMIT_BLOCKED */
      nameField.touch();
      shortNameField.touch();
      sortOrderField.touch();
      workloadField.touch();
      return;
      /* END_BLOCK_SUBMIT_BLOCKED */
    }
    setLoading(true);
    try {
      const workloadValue = Number(workloadField.value);
      await onUpdate(role.id, {
        name: nameField.value.trim(),
        shortName: shortNameField.value.trim(),
        sortOrder: Number(sortOrderField.value),
        workloadPercent: workloadValue,
        concurrentRoleIds: concurrentRoles.map((r) => r.id),
      });
      /* START_BLOCK_SUBMIT_OK */
      onClose();
      /* END_BLOCK_SUBMIT_OK */
    } finally {
      setLoading(false);
    }
  };
  /* END_METHOD_handleUpdate */

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      slotProps={{ transition: dialogTransitionSlotProps }}
    >
      <form onSubmit={handleUpdate} noValidate>
        <DialogTitle sx={{ fontSize: '1rem' }}>Редактирование проектной роли</DialogTitle>
        <DialogContent>
          <TextField
            inputRef={firstFieldRef}
            required
            fullWidth
            label="Название"
            value={nameField.value}
            onChange={nameField.onChange}
            onBlur={nameField.onBlur}
            margin="normal"
            size="small"
            disabled={loading}
            error={nameField.showError}
            helperText={nameField.showError ? 'Введите название роли' : ' '}
            slotProps={{ htmlInput: { maxLength: 200 } }}
          />
          <TextField
            required
            fullWidth
            label="Сокращённое название"
            value={shortNameField.value}
            onChange={shortNameField.onChange}
            onBlur={shortNameField.onBlur}
            margin="normal"
            size="small"
            disabled={loading}
            error={shortNameField.showError}
            helperText={shortNameField.showError ? 'Введите сокращённое название' : ' '}
            slotProps={{ htmlInput: { maxLength: 50 } }}
          />
          <TextField
            required
            fullWidth
            label="Порядок сортировки"
            value={sortOrderField.value}
            onChange={(e) => sortOrderField.setValue(e.target.value.replace(/[^\d-]/g, ''))}
            onBlur={sortOrderField.onBlur}
            margin="normal"
            size="small"
            disabled={loading}
            type="text"
            inputMode="numeric"
            error={sortOrderField.showError}
            helperText={sortOrderField.showError ? 'Укажите порядок сортировки' : ' '}
          />
          <TextField
            fullWidth
            label="Процент загрузки"
            value={workloadField.value}
            onChange={(e) => workloadField.setValue(e.target.value.replace(/[^\d]/g, ''))}
            onBlur={workloadField.onBlur}
            margin="normal"
            size="small"
            required
            disabled={loading}
            type="text"
            inputMode="numeric"
            error={workloadField.showError}
            helperText={workloadField.showError ? 'Целое число от 0 до 100' : ' '}
            slotProps={{ htmlInput: { min: 0, max: 100 } }}
          />
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 0.5, mb: 1 }}>
            <RoleWorkloadBar
              value={previewWorkload}
              accentColor={previewAccent}
              size="lg"
            />
            <Box component="span" sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
              Так загрузка будет отображаться в списке ролей
            </Box>
          </Box>
        <ConcurrentRolesSelect
          label="Одновременная работа с ролями"
          roles={existingRoles}
          excludeRoleId={role.id}
          value={concurrentRoles}
          onChange={setConcurrentRoles}
          disabled={loading}
        />
      </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button size="small" onClick={handleClose} disabled={loading}>
            Отмена
          </Button>
          <Button
            type="submit"
            variant="contained"
            color="success"
            size="small"
            loading={loading}
            disabled={!canSave}
          >
            Сохранить
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
/* END_METHOD_EditProjectRoleDialog */
